"use strict";
/**
 * Health Check Utilities
 * Helper functions for dependency health checks (PostgreSQL, Redis, RabbitMQ)
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.checkPostgreSQLHealth = checkPostgreSQLHealth;
exports.checkRedisHealth = checkRedisHealth;
exports.checkRabbitMQHealth = checkRabbitMQHealth;
exports.determineReadinessStatus = determineReadinessStatus;
/**
 * Check PostgreSQL connection health
 * Uses ProductionDatabase from @teletienda/sql-orm
 * @param db Database instance (ProductionDatabase from @teletienda/sql-orm)
 * @param timeout Timeout in milliseconds (default: 2000ms)
 */
async function checkPostgreSQLHealth(db, timeout = 2000) {
    const startTime = Date.now();
    try {
        if (typeof db.query !== 'function') {
            throw new Error('Invalid database instance (missing query method)');
        }
        // Use Promise.race to enforce timeout
        await Promise.race([
            db.query('SELECT 1'),
            new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout')), timeout))
        ]);
        const latency = Date.now() - startTime;
        return {
            status: 'up',
            latency,
            lastCheck: new Date().toISOString()
        };
    }
    catch (error) {
        const latency = Date.now() - startTime;
        return {
            status: 'down',
            latency,
            error: error.message || 'Connection failed',
            lastCheck: new Date().toISOString()
        };
    }
}
/**
 * Check Redis connection health
 * @param redisClient Redis client instance
 * @param timeout Timeout in milliseconds (default: 1000ms)
 */
async function checkRedisHealth(redisClient, timeout = 1000) {
    const startTime = Date.now();
    try {
        // Use Promise.race to enforce timeout
        await Promise.race([
            redisClient.ping(),
            new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout')), timeout))
        ]);
        const latency = Date.now() - startTime;
        return {
            status: 'up',
            latency,
            lastCheck: new Date().toISOString()
        };
    }
    catch (error) {
        const latency = Date.now() - startTime;
        return {
            status: 'down',
            latency,
            error: error.message || 'PING failed',
            lastCheck: new Date().toISOString()
        };
    }
}
/**
 * Check RabbitMQ connection health
 * @param connection RabbitMQ connection instance (amqplib)
 * @param timeout Timeout in milliseconds (default: 2000ms)
 */
async function checkRabbitMQHealth(connection, timeout = 2000) {
    const startTime = Date.now();
    try {
        // Check if connection exists and is not closed
        if (!connection) {
            throw new Error('Connection is null');
        }
        // RabbitMQ connection has a 'connection' property when healthy
        // If connection is closed, this will throw
        const isConnected = connection.connection && !connection.connection.closing;
        if (!isConnected) {
            throw new Error('Connection is closing or closed');
        }
        const latency = Date.now() - startTime;
        return {
            status: 'up',
            latency,
            lastCheck: new Date().toISOString()
        };
    }
    catch (error) {
        const latency = Date.now() - startTime;
        return {
            status: 'down',
            latency,
            error: error.message || 'Connection check failed',
            lastCheck: new Date().toISOString()
        };
    }
}
/**
 * Determine overall readiness status from dependencies
 * @param dependencies Map of dependency health checks
 * @returns 'ready' if all critical dependencies are up, 'not_ready' otherwise
 */
function determineReadinessStatus(dependencies) {
    // Service is NOT ready if any dependency is down
    const hasDownDependency = Object.values(dependencies).some(dep => dep.status === 'down');
    return hasDownDependency ? 'not_ready' : 'ready';
}
